// Servicio de API para formularios inteligentes
// Conecta los formularios con endpoints reales del backend MGI
import { ref, readonly } from 'vue'
import { apiClient as apiClient } from './api-client'
import { extractInventoryError } from './errorMaps/inventory'
import { measureApi } from './utils/measureApi'
import { buildStandardHeaders } from './utils/headers'

export interface ApiResponse<T = any> {
  success: boolean
  data: T
  message?: string
  errors?: Record<string, string[]>
}

export interface ValidationResponse {
  valid: boolean
  errors: Record<string, string>
  suggestions?: Record<string, string[]>
}

export interface FileUploadResponse {
  success: boolean
  fileId: string
  fileName: string
  fileUrl: string
  fileSize: number
}

// Servicio para validaciones en tiempo real
export class FormValidationService {
  /**
   * Valida un email en tiempo real contra la base de datos
   */
  static async validateEmailUniqueness(email: string, excludeUserId?: string): Promise<ValidationResponse> {
    try {
      const params = new URLSearchParams({ email })
      if (excludeUserId) params.append('excludeId', excludeUserId)
  const response = await apiClient.get(`/users/validate-email?${params.toString()}`, { headers: { ...buildStandardHeaders({ 'x-skip-auth-redirect': 'true' }) }, skipAuthRedirect: true } as any)
      const data = (response as any).data || response
      const isAvailable = data.isAvailable ?? data.data?.isAvailable
      return { valid: !!isAvailable, errors: isAvailable ? {} : { email: 'Este email ya está en uso' } }
    } catch (error) {
      console.error('Error validating email:', error)
      return { valid: false, errors: { email: 'Error al validar email' } }
    }
  }

  /**
   * Valida un nombre de usuario único
   */
  static async validateUsernameUniqueness(username: string, excludeUserId?: string): Promise<ValidationResponse> {
    try {
      const params = new URLSearchParams({ username })
      if (excludeUserId) params.append('excludeId', excludeUserId)
  const response = await apiClient.get(`/users/validate-username?${params.toString()}`, { headers: { ...buildStandardHeaders({ 'x-skip-auth-redirect': 'true' }) }, skipAuthRedirect: true } as any)
      const data = (response as any).data || response
      const isAvailable = data.isAvailable ?? data.data?.isAvailable
      return { valid: !!isAvailable, errors: isAvailable ? {} : { username: 'Este nombre de usuario ya está en uso' } }
    } catch (error) {
      console.error('Error validating username:', error)
      return { valid: false, errors: { username: 'Error al validar nombre de usuario' } }
    }
  }


  /**
   * Busca agencias con autocompletado
   */
  static async searchAgencies(query: string, limit: number = 10): Promise<any[]> {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>('/agencies/search', {
        params: { q: query, limit },
        headers: buildStandardHeaders()
      })
      return response.data.data
    } catch (error) {
      console.error('Error searching agencies:', error)
      return []
    }
  }

  /**
   * Busca productos en inventario
   */
  static async searchProducts(query: string, agencyId?: string, limit: number = 20): Promise<any[]> {
    try {
      const response = await measureApi(() => apiClient.get<ApiResponse<any[]>>('/inventory/products/search', {
        params: { q: query, agencyId, limit },
        headers: buildStandardHeaders()
      }), { label: 'form.searchProducts', meta: { qLen: query.length, agency: !!agencyId } })
      return (response as any).data.data
    } catch (error:any) {
      const mapped = extractInventoryError(error)
  console.warn('Error searching products (mapped):', (mapped as any).code)
      return []
    }
  }
}

// Servicio para subida de archivos
export class FileUploadService {
  /**
   * Sube un archivo al servidor
   */
  static async uploadFile(file: File, context: 'incident' | 'user' | 'inventory'): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('context', context)

    try {
      const response = await apiClient.post<ApiResponse<FileUploadResponse>>('/files/upload', formData, {
        headers: buildStandardHeaders({ 'Content-Type': 'multipart/form-data' }),
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            console.log(`Upload progress: ${percentCompleted}%`)
          }
        }
      })
      return response.data.data
    } catch (error) {
      console.error('Error uploading file:', error)
      throw new Error('Error al subir archivo')
    }
  }

  /**
   * Sube múltiples archivos
   */
  static async uploadMultipleFiles(files: File[], context: 'incident' | 'user' | 'inventory'): Promise<FileUploadResponse[]> {
    const uploadPromises = files.map(file => this.uploadFile(file, context))
    return Promise.all(uploadPromises)
  }

  /**
   * Elimina un archivo del servidor
   */
  static async deleteFile(fileId: string): Promise<boolean> {
    try {
      await apiClient.delete(`/files/${fileId}`)
      return true
    } catch (error) {
      console.error('Error deleting file:', error)
      return false
    }
  }
}

// P13-V1D (C10, R0-INC-09): IncidentFormService fue RETIRADO — cliente paralelo sin consumidores
// con rutas phantom (/incidents/drafts, /incidents/categories/autocomplete). La creación/edición
// canónica de incidencias vive en incidentsService.

// Servicio específico para usuarios
export class UserFormService {
  /**
   * Crea un nuevo usuario
   */
  static async createUser(userData: any): Promise<ApiResponse> {
    try {
      // Subir avatar si existe
      if (userData.avatar instanceof File) {
        const avatarResponse = await FileUploadService.uploadFile(userData.avatar, 'user')
        userData.avatarUrl = avatarResponse.fileUrl
        delete userData.avatar
      }

  const response = await apiClient.post<ApiResponse>('/users', userData, { headers: buildStandardHeaders() })
      return response.data
    } catch (error) {
      throw this.handleApiError(error)
    }
  }

  /**
   * Actualiza un usuario existente
   */
  static async updateUser(userId: string, userData: any): Promise<ApiResponse> {
    try {
      // Subir nuevo avatar si existe
      if (userData.avatar instanceof File) {
        const avatarResponse = await FileUploadService.uploadFile(userData.avatar, 'user')
        userData.avatarUrl = avatarResponse.fileUrl
        delete userData.avatar
      }

  const response = await apiClient.put<ApiResponse>(`/users/${userId}`, userData, { headers: buildStandardHeaders() })
      return response.data
    } catch (error) {
      throw this.handleApiError(error)
    }
  }

  /**
   * Obtiene roles disponibles
   */
  static async getAvailableRoles(): Promise<any[]> {
    try {
  const response = await apiClient.get<ApiResponse<any[]>>('/users/roles', { headers: buildStandardHeaders() })
      return response.data.data
    } catch (error) {
      console.error('Error getting roles:', error)
      return []
    }
  }

  /**
   * Obtiene permisos disponibles
   */
  static async getAvailablePermissions(): Promise<any[]> {
    try {
  const response = await apiClient.get<ApiResponse<any[]>>('/users/permissions', { headers: buildStandardHeaders() })
      return response.data.data
    } catch (error) {
      console.error('Error getting permissions:', error)
      return []
    }
  }

  private static handleApiError(error: any): Error {
    if (error.response?.data?.errors) {
      const errorMessages = Object.values(error.response.data.errors).flat().join(', ')
      return new Error(errorMessages)
    }
    return new Error(error.response?.data?.message || 'Error en la solicitud')
  }
}

// Servicio específico para solicitudes de inventario
export class InventoryRequestService {
  /**
   * Crea una nueva solicitud de inventario
   */
  static async createRequest(requestData: any): Promise<ApiResponse> {
    try {
      // Delegar en inventoryService para reutilizar normalización + idempotencia
  const mod = await import('./inventoryService')
  const inventoryService = (mod as any).default || (mod as any)
      const created = await inventoryService.createStockRequest(requestData)
      return { success: true, data: created }
    } catch (error:any) {
      const mapped = extractInventoryError(error)
      throw mapped
    }
  }

  /**
   * Actualiza una solicitud existente
   */
  static async updateRequest(requestId: string, requestData: any): Promise<ApiResponse> {
    try {
      // Aún no existe método unificado en inventoryService para update; mantenemos llamada directa con measureApi
      const response = await measureApi(() => apiClient.put<ApiResponse>(`/inventory/requests/${requestId}`, requestData), { label: 'formInventory.updateRequest', meta: { id: requestId } })
      return (response as any).data
    } catch (error:any) {
      throw extractInventoryError(error)
    }
  }

  /**
   * Obtiene stock disponible de un producto
   */
  static async getProductStock(productId: string, agencyId?: string): Promise<number> {
    try {
      const response = await measureApi(() => apiClient.get<ApiResponse<{ stock: number }>>(`/inventory/products/${productId}/stock`, {
        params: { agencyId },
        headers: buildStandardHeaders()
      }), { label: 'formInventory.getProductStock', meta: { agency: !!agencyId } })
      return (response as any).data.data.stock
    } catch (error:any) {
      const mapped = extractInventoryError(error)
  console.warn('Stock lookup fallback (mapped):', (mapped as any).code)
      return 0
    }
  }

  /**
   * Valida disponibilidad de productos
   */
  static async validateProductAvailability(products: Array<{ id: string, quantity: number }>): Promise<ValidationResponse> {
    try {
  const response = await measureApi(() => apiClient.post<ApiResponse<ValidationResponse>>('/inventory/validate-availability', { products }, { headers: buildStandardHeaders() }), { label: 'formInventory.validateAvailability', meta: { count: products.length } })
      return (response as any).data.data
    } catch (error:any) {
      const mapped = extractInventoryError(error)
  console.warn('Availability validation error (mapped):', (mapped as any).code)
      return { valid: false, errors: { products: mapped.message || 'Error al validar disponibilidad' } }
    }
  }

  private static handleApiError(error: any): Error {
    if (error.response?.data?.errors) {
      const errorMessages = Object.values(error.response.data.errors).flat().join(', ')
      return new Error(errorMessages)
    }
    return new Error(error.response?.data?.message || 'Error en la solicitud')
  }
}

// Hook de Vue para integración con APIs
export const useApiIntegration = () => {
  const loading = ref(false)
  const error = ref<string | null>(null)

  const withLoading = async <T>(operation: () => Promise<T>): Promise<T | null> => {
    loading.value = true
    error.value = null

    try {
      const result = await operation()
      return result
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error desconocido'
      console.error('API operation failed:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  return {
    loading: readonly(loading),
    error: readonly(error),
    withLoading,
    clearError: () => { error.value = null }
  }
}

export default {
  FormValidationService,
  FileUploadService,
  UserFormService,
  InventoryRequestService
}
